import { useCallback, useEffect, useRef, useState } from 'react';
import { useCallOperatorCurrentCall, useCallOperatorEventListener, useCallOperatorState } from '../lib/callixSdk';
import { checkAudioDevices, pickNotice, type AudioIssue, type CallFailureLike, type DialerNotice } from '../lib/dialerDiagnostics';

/**
 * Junta tudo o que pode estar impedindo o agente de ligar num aviso só:
 * microfone/saída de som (verificados ao abrir e a cada troca de dispositivo),
 * erro de partida do operador, sessão derrubada pelo Callix, chamada que falhou
 * (com o motivo que o Callix mandou) e serviço offline. Deve ser usado dentro do
 * CallixClientProvider.
 */
export interface DialerNoticesOptions {
  /** quanto tempo o aviso de chamada falhada fica na tela (ms) */
  callFailureTtlMs?: number;
  /** desliga a verificação de dispositivos (testes) */
  checkDevices?: boolean;
}

export interface DialerNotices {
  notice: DialerNotice | null;
  deviceIssue: AudioIssue | null;
  /** o aviso atual pode ser fechado pelo agente (chamada falhada, sessão derrubada) */
  dismiss: () => void;
  /** refaz a verificação de dispositivos (ex.: botão "Verificar de novo") */
  recheckDevices: () => Promise<void>;
}

type CallLike = { on?: (event: string, listener: (event: { failure?: CallFailureLike }) => void) => unknown; off?: (event: string, listener: (event: { failure?: CallFailureLike }) => void) => unknown; toCallData?: () => { failure?: CallFailureLike } } | undefined;

export function useDialerNotices(options: DialerNoticesOptions = {}): DialerNotices {
  const operatorState = useCallOperatorState();
  const currentCall = useCallOperatorCurrentCall() as CallLike;
  const [deviceIssue, setDeviceIssue] = useState<AudioIssue | null>(null);
  const [sessionDrop, setSessionDrop] = useState<string | null>(null);
  const [callFailure, setCallFailure] = useState<CallFailureLike | null>(null);
  const [dismissedCode, setDismissedCode] = useState<string | null>(null);
  const failureTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ttl = options.callFailureTtlMs ?? 20_000;

  const recheckDevices = useCallback(async () => {
    if (options.checkDevices === false) return;
    const issue = await checkAudioDevices();
    setDeviceIssue(issue);
  }, [options.checkDevices]);

  // dispositivos: ao abrir e a cada conexão/desconexão de fone
  useEffect(() => {
    if (options.checkDevices === false) return;
    let cancelled = false;
    const run = async () => {
      const issue = await checkAudioDevices();
      if (!cancelled) setDeviceIssue(issue);
    };
    void run();
    const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    const handler = () => void run();
    mediaDevices?.addEventListener?.('devicechange', handler);
    return () => {
      cancelled = true;
      mediaDevices?.removeEventListener?.('devicechange', handler);
    };
  }, [options.checkDevices]);

  // sessão derrubada pelo Callix (login em outro lugar, supervisor, bloqueio, limites)
  useCallOperatorEventListener('userSessionDropped', (data) => {
    setSessionDrop(data?.originator ?? 'unknown');
  });

  // falha de chamada: o motivo chega no evento `failed` da chamada corrente
  useEffect(() => {
    if (!currentCall?.on) return;
    const handleFailed = (event: { failure?: CallFailureLike }) => {
      const failure = event?.failure ?? currentCall.toCallData?.().failure ?? null;
      setCallFailure(failure ?? { cause: null });
      setDismissedCode(null);
      if (failureTimer.current) clearTimeout(failureTimer.current);
      failureTimer.current = setTimeout(() => setCallFailure(null), ttl);
    };
    currentCall.on('failed', handleFailed);
    return () => {
      currentCall.off?.('failed', handleFailed);
    };
  }, [currentCall, ttl]);

  // estado manualCallFailed sem evento (ex.: a chamada nem foi criada): usa o que a chamada traz
  useEffect(() => {
    if (operatorState?.state !== 'manualCallFailed') return;
    const failure = (operatorState as { call?: { toCallData?: () => { failure?: CallFailureLike } } }).call?.toCallData?.().failure ?? null;
    setCallFailure((current) => current ?? failure ?? { cause: null });
    setDismissedCode(null);
  }, [operatorState]);

  // chamada em andamento ou nova chamada: o aviso de falha anterior sai
  useEffect(() => {
    if (operatorState?.state === 'callInProgress' || operatorState?.state === 'manualCallRinging' || operatorState?.state === 'callRinging') {
      setCallFailure(null);
    }
    if (operatorState?.state === 'idle' || operatorState?.state === 'onBreak') setSessionDrop(null);
  }, [operatorState?.state]);

  useEffect(() => () => {
    if (failureTimer.current) clearTimeout(failureTimer.current);
  }, []);

  const notice = pickNotice({
    deviceIssue,
    operatorState: operatorState?.state ?? null,
    startupError: operatorState?.state === 'startupError' ? (operatorState as { error?: string }).error ?? null : null,
    sessionDrop,
    callFailure,
  });
  const visible = notice && notice.code !== dismissedCode ? notice : null;

  const dismiss = useCallback(() => {
    if (!notice) return;
    if (notice.source === 'call') setCallFailure(null);
    else if (notice.source === 'session') setSessionDrop(null);
    else setDismissedCode(notice.code);
  }, [notice]);

  return { notice: visible, deviceIssue, dismiss, recheckDevices };
}
